import { useRef, useState } from 'react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

// Drag to reorder: grab a row's handle, drag it up or down, let go. The other
// rows slide out of the way as it passes. Pointer events, so a mouse, a pen or
// a finger; the handle also takes the arrow keys, one place at a time.
//
//   <SortableList items={steps} keyOf={(s, i) => i} onMove={(from, to) => …}>
//     {(item, index, handle) => <Row …><Grip {...handle} /></Row>}
//   </SortableList>
//
// onMove(from, to) is told where the row went; the parent reorders its own list.

const GAP = 8;   // px between rows (space-y-2)

export function moveItem(list, from, to) {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export default function SortableList({ items, keyOf, onMove, children, disabled = false, className = '' }) {
  const rows = useRef([]);
  const [drag, setDrag] = useState(null);   // { from, to, dy, height }

  const start = (index) => (e) => {
    if (disabled || (e.button !== undefined && e.button !== 0)) return;
    e.preventDefault();
    const tops = rows.current.slice(0, items.length).map((el) => el.getBoundingClientRect().top + window.scrollY);
    const heights = rows.current.slice(0, items.length).map((el) => el.offsetHeight);
    const startY = e.pageY;
    let state = { from: index, to: index, dy: 0, height: heights[index] };
    setDrag(state);

    const move = (ev) => {
      // near the top or bottom of the window: scroll, for a long list
      if (ev.clientY < 70) window.scrollBy(0, -14);
      else if (ev.clientY > window.innerHeight - 70) window.scrollBy(0, 14);
      const dy = ev.pageY - startY;
      const centre = tops[index] + dy + heights[index] / 2;
      // where it would land: after every other row whose middle it has passed
      let to = 0;
      tops.forEach((top, i) => {
        if (i !== index && centre > top + heights[i] / 2) to += 1;
      });
      state = { ...state, dy, to };
      setDrag(state);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      setDrag(null);
      if (state.to !== state.from) onMove(state.from, state.to);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  const keys = (index) => (e) => {
    if (disabled) return;
    if (e.key === 'ArrowUp' && index > 0) { e.preventDefault(); onMove(index, index - 1); }
    if (e.key === 'ArrowDown' && index < items.length - 1) { e.preventDefault(); onMove(index, index + 1); }
  };

  // how far each row is pushed while another is dragged past it
  const shift = (i) => {
    if (!drag || i === drag.from) return 0;
    const by = drag.height + GAP;
    if (drag.from < drag.to && i > drag.from && i <= drag.to) return -by;
    if (drag.from > drag.to && i < drag.from && i >= drag.to) return by;
    return 0;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {items.map((item, i) => {
        const dragging = drag?.from === i;
        const handle = {
          onPointerDown: start(i),
          onKeyDown: keys(i),
          role: 'button',
          tabIndex: disabled ? -1 : 0,
          'aria-label': 'Drag to reorder (or use the arrow keys)',
          style: { touchAction: 'none', cursor: disabled ? 'default' : dragging ? 'grabbing' : 'grab' },
        };
        return (
          <div key={keyOf(item, i)} ref={(el) => { rows.current[i] = el; }}
            className={dragging ? 'relative z-20' : 'relative'}
            style={{
              transform: `translateY(${dragging ? drag.dy : shift(i)}px)`,
              transition: dragging ? 'none' : drag ? 'transform 150ms ease' : 'none',
              boxShadow: dragging ? '0 10px 25px rgba(0,0,0,0.15)' : undefined,
              borderRadius: dragging ? 8 : undefined,
            }}>
            {children(item, i, handle)}
          </div>
        );
      })}
    </div>
  );
}

// The handle itself: six dots, as everywhere else that drags.
export function Grip(props) {
  return (
    <span {...props} className="flex h-8 w-6 shrink-0 select-none items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand">
      <svg viewBox="0 0 10 16" className="h-4 w-2.5" fill="currentColor" aria-hidden="true">
        {[2, 8, 14].map((y) => [2, 8].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.5" />))}
      </svg>
    </span>
  );
}

// One line for a question or scene in the list: the drag handle, its number
// and what it is, and a bin. Clicking the line opens it to edit.
export function ItemHeader({ handle, locked, open, onToggle, onRemove, number, tag, title, thumb, problem }) {
  return (
    <div className="flex items-center gap-2">
      {!locked ? <Grip {...handle} /> : null}
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="flex min-w-0 flex-1 items-center gap-2 rounded py-1 text-left hover:bg-gray-50">
        <span className="w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-400">{number}</span>
        {thumb ? <img src={thumb} alt="" className="h-10 w-6 shrink-0 rounded object-cover" /> : null}
        {tag ? (
          <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{tag}</span>
        ) : null}
        <span className={`truncate text-sm ${title ? 'text-gray-900' : 'italic text-gray-400'}`}>{title || 'Not written yet'}</span>
        {problem ? <span className="shrink-0 text-xs font-medium text-rose-600">{problem}</span> : null}
        <ChevronDownIcon className={`ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {!locked ? (
        <button type="button" aria-label="Remove" onClick={onRemove}
          className="rounded p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
      ) : null}
    </div>
  );
}
